import { clone } from '@vestigas/utils';

import { AttachmentObject } from './attachmentUtils';
import { ATTACHMENT_TYPE } from './constants';
import {
  type AttachmentCreatedObject,
  type AttachmentTypeType,
  type DeliveryNoteActionCreatedObject,
} from './types';

export const DeliveryNoteAttachmentHandlerObject = {
  /**
   * Adds accept article attachments from legacy data.
   * @param assetChain - The asset chain containing the legacy data.
   */
  addAcceptArticleAttachmentsFromChain_legacy(assetChain: Record<string, any>) {
    if (!assetChain.meta?.acceptEvent?.acceptedItems) {
      return;
    }

    for (const acceptedItem of assetChain.meta.acceptEvent.acceptedItems) {
      if (!acceptedItem.attachments) {
        continue;
      }

      for (const chainAttachment of acceptedItem.attachments) {
        const attachment = AttachmentObject.create({
          blob: chainAttachment.blob,
          chainId: assetChain.id,
          type: chainAttachment.type,
        });

        if (
          this.attachments.some(
            (existingAttachment) =>
              existingAttachment.digest === attachment.digest,
          )
        ) {
          continue;
        }

        this.attachments.push(attachment);
      }
    }
  },

  /**
   * Add the attachments from different places of the asset chain to the attachment handler.
   * @param assetChain - The asset chain containing attachments.
   */
  addAttachmentsFromChain(assetChain: Record<string, any>) {
    this.addGeneralAttachmentFromChain(assetChain);
    this.addGeneralAttachmentsFromChain(assetChain);
    this.addAcceptArticleAttachmentsFromChain_legacy(assetChain);
  },

  /**
   * Adds a general attachment from the asset chain if available.
   * @param assetChain - The asset chain containing the attachment.
   */
  addGeneralAttachmentFromChain(assetChain: Record<string, any>) {
    if (!assetChain.attachment) {
      return;
    }

    const attachment = AttachmentObject.create({
      blob: assetChain.attachment.blob,
      chainId: assetChain.id,
      type: assetChain.attachment.type ?? assetChain.attachment.comment, // the type is apparently stored in a comment
    });

    this.attachments.push(attachment);
  },

  /**
   * Adds all general attachments from the asset chain to the handler.
   * @param assetChain - The asset chain containing the attachments.
   */
  addGeneralAttachmentsFromChain(assetChain: Record<string, any>) {
    if (!assetChain.attachments) {
      return;
    }

    for (const path of Object.keys(assetChain.attachments)) {
      const blob = assetChain.attachments[path];
      let pathArray = ['attachments', ...path.split('/')];
      pathArray = pathArray.filter(
        (pathItem) => pathItem !== 'blob_' && pathItem !== 'blob',
      );

      let type: AttachmentTypeType = ATTACHMENT_TYPE.DELIVERY;
      if (path === 'originalPdf') {
        type = ATTACHMENT_TYPE.DELIVERY;
      }

      const attachment = AttachmentObject.create({
        blob,
        chainId: assetChain.id,
        type,
      });

      if (
        this.attachments.some(
          (existingAttachment) =>
            existingAttachment.digest === attachment.digest,
        )
      ) {
        continue;
      }

      this.attachments.push(attachment);
    }
  },

  attachments: [] as AttachmentCreatedObject[],

  /**
   * Creates a new DeliveryNoteAttachmentHandler instance with default values.
   * @returns - The DeliveryNoteAttachmentHandler instance.
   */
  create() {
    return clone(this);
  },

  /**
   * Populates the accept article chain IDs in the delivery note.
   * @param deliveryNote - The delivery note object to update.
   */
  populateAcceptArticleChainIds(deliveryNote: Array<Record<string, any>>) {
    for (const article of deliveryNote.articles) {
      this.populateChainIdForAttachments(
        article.acceptArticleSupplier.attachments,
        deliveryNote,
      );
      this.populateChainIdForAttachments(
        article.acceptArticleCarrier.attachments,
        deliveryNote,
      );
      this.populateChainIdForAttachments(
        article.acceptArticleRecipient.attachments,
        deliveryNote,
      );
      this.populateChainIdForAttachments(
        article.acceptArticleOnBehalfSupplier.attachments,
        deliveryNote,
      );
      this.populateChainIdForAttachments(
        article.acceptArticleOnBehalfCarrier.attachments,
        deliveryNote,
      );
      this.populateChainIdForAttachments(
        article.acceptArticleOnBehalfRecipient.attachments,
        deliveryNote,
      );
    }
  },

  /**
   * Helper method to populate chain IDs for attachments in articles.
   * @param attachments - The list of attachments to process.
   * @param deliveryNote - The delivery note to check for matching attachments.
   */
  populateChainIdForAttachments(
    attachments: AttachmentCreatedObject[],
    deliveryNote: Record<string, any>,
  ) {
    for (const acceptArticleAttachment of attachments) {
      const attachment = deliveryNote.attachmentHandler.attachments.find(
        (attachment) => attachment.digest === acceptArticleAttachment.digest,
      );
      if (!attachment) {
        continue;
      }

      acceptArticleAttachment.chainId = attachment.chainId;
    }
  },

  /**
   * Populates delivery note actions with the corresponding attachments.
   * @param deliveryNoteActions - The list of delivery note actions to update.
   */
  populateDeliveryNoteActions(
    deliveryNoteActions: DeliveryNoteActionCreatedObject[],
  ) {
    for (const deliveryNoteAction of deliveryNoteActions) {
      deliveryNoteAction.attachments = this.attachments.filter(
        ({ chainId }) => chainId === deliveryNoteAction.id,
      );
    }
  },
};
